import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Navigate, useLocation } from 'react-router-dom'
import { TopNavigation } from '@/components/TopNavigation'
import { ColorSquare } from '@/components/ColorSquare'
import { TestDeviceIcon } from '@/components/TestDeviceIcon'
import { Submenu } from '@/components/Submenu'
import { Toast } from '@/components/Toast'
import { PatientId } from '@/components/PatientId'
import { DeviceTestsTab } from '@/components/DeviceTestsTab'
import { DeviceDashboardTab } from '@/components/DeviceDashboardTab'
import { useAuth } from '@/hooks/useAuth'
import {
  DeviceService,
  UserProfileHelper,
  DeviceTestService,
  supabase
} from '@lura/shared'
import type {
  ClinicianDevice,
  DeviceUser,
  DeviceDataRow,
  DeviceTest
} from '@lura/shared'

export const ClinicianDeviceDetailPage: React.FC = () => {
  const { deviceId } = useParams<{ deviceId: string }>()
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [device, setDevice] = useState<ClinicianDevice | null>(null)
  const [deviceUsers, setDeviceUsers] = useState<DeviceUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Raw data state
  const [rawDataRows, setRawDataRows] = useState<DeviceDataRow[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRows, setTotalRows] = useState(0)
  const rowsPerPage = 50

  // Tests state
  const [deviceTests, setDeviceTests] = useState<DeviceTest[]>([])
  const [testsLoading, setTestsLoading] = useState(false)

  // Submenu config
  const submenuItems = [
    { id: 'dashboard', label: 'Dashboard', href: `/clinician/devices/${deviceId}` },
    { id: 'tests', label: 'Tests', href: `/clinician/devices/${deviceId}/tests` },
    { id: 'studies', label: 'Studies', href: `/clinician/devices/${deviceId}/studies` },
    { id: 'users', label: 'Users', href: `/clinician/devices/${deviceId}/users` },
    { id: 'raw-data', label: 'Raw Data', href: `/clinician/devices/${deviceId}/raw-data` }
  ]

  const activeTab = location.pathname.endsWith('/studies')
    ? 'studies'
    : location.pathname.endsWith('/users')
      ? 'users'
      : location.pathname.endsWith('/raw-data')
        ? 'raw-data'
        : location.pathname.endsWith('/tests')
          ? 'tests'
          : 'dashboard'

  // Clinician access check
  if (!UserProfileHelper.isClinician(user)) {
    return <Navigate to="/patients" replace />
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!deviceId || !user?.profile) {
        setError('Device ID is required')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Fetch device details - use getClinicianDevices and filter
        const allDevices = await DeviceService.getClinicianDevices(
          user.profile.id
        )
        const deviceDetails = allDevices.find(d => d.id === deviceId)

        if (!deviceDetails) {
          setError('Device not found')
          setLoading(false)
          return
        }
        setDevice(deviceDetails)

        // Fetch device users
        const users = await DeviceService.getDeviceUsers(deviceId)
        setDeviceUsers(users)
      } catch (err) {
        console.error('Error fetching data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [deviceId, user?.profile?.id])

  // Fetch raw data when on raw-data tab
  useEffect(() => {
    const fetchRawData = async () => {
      if (!deviceId || activeTab !== 'raw-data') return

      try {
        const offset = (currentPage - 1) * rowsPerPage

        // Get total count
        const { count } = await supabase
          .from('device_data')
          .select('*', { count: 'exact', head: true })
          .eq('device_id', deviceId)

        if (count !== null) {
          setTotalRows(count)
        }

        // Get paginated data
        const { data, error } = await supabase
          .from('device_data')
          .select('*')
          .eq('device_id', deviceId)
          .order('recorded_at', { ascending: false })
          .range(offset, offset + rowsPerPage - 1)

        if (error) {
          console.error('Error fetching raw data:', error)
          return
        }

        if (data) {
          setRawDataRows(data as DeviceDataRow[])
        }
      } catch (err) {
        console.error('Error fetching raw data:', err)
      }
    }

    fetchRawData()
  }, [deviceId, activeTab, currentPage])

  // Fetch device tests when on tests tab
  useEffect(() => {
    const fetchTests = async () => {
      if (!deviceId || activeTab !== 'tests') return

      try {
        setTestsLoading(true)
        const tests = await DeviceTestService.getDeviceTests(deviceId)
        setDeviceTests(tests)
      } catch (err) {
        console.error('Error fetching device tests:', err)
      } finally {
        setTestsLoading(false)
      }
    }

    fetchTests()
  }, [deviceId, activeTab])

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  return (
    <div className="min-h-screen bg-off-white">
      <TopNavigation
        currentPage="Devices"
        onSignOut={handleSignOut}
        user={user}
      />

      <main className="max-w-7xl mx-auto py-8 px-6">
        <div className="px-4 pb-6 sm:px-0">
          {/* Back Button */}
          <button
            onClick={() => navigate('/clinician/devices')}
            className="mb-4 flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Devices
          </button>

          {/* Error State */}
          {error && (
            <div className="mb-6 rounded-3xl bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          )}

          {/* Page Header */}
          {device && (
            <div className="mb-8">
              <div>
                <h2
                  className="text-5xl font-normal text-dark-blue flex items-center gap-3"
                  style={{ fontFamily: 'Family' }}
                >
                  {device.serial_number}
                  {device.is_test_device && <TestDeviceIcon size="xl" />}
                </h2>
                <p className="mt-2 text-xs text-gray-500">
                  Created{' '}
                  {new Date(device.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          )}

          {/* Submenu */}
          {device && (
            <div className="mb-8">
              <Submenu items={submenuItems} activeItem={activeTab} />
            </div>
          )}

          {/* Main Content */}
          {!loading && device && (
            <div className="space-y-6">
              {/* Dashboard Tab */}
              {activeTab === 'dashboard' && (
                <DeviceDashboardTab deviceId={device.id} />
              )}

              {/* Studies Tab */}
              {activeTab === 'studies' && (
                <>
                  {/* Assigned Study Section */}
                  {device.study_id && (
                    <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">
                          Assigned Study
                        </h2>
                      </div>

                      <div className="px-6 py-4 flex items-center hover:bg-gray-50 transition-colors">
                        {device.study_color && (
                          <ColorSquare color={device.study_color} size="md" />
                        )}
                        <div className="ml-4 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {device.study_title}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {!device.study_id && (
                    <div className="bg-white shadow-sm rounded-3xl p-12 text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p className="text-sm text-gray-500">No study assigned yet</p>
                    </div>
                  )}
                </>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Device Users
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Patients who have used this device
                    </p>
                  </div>

                  {deviceUsers.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-highlight-green">
                          <tr>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                            >
                              Patient ID
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                            >
                              Last Used
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {deviceUsers.map(deviceUser => (
                            <tr key={deviceUser.user_id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  <PatientId patientId={deviceUser.user_id} />
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">
                                  {new Date(deviceUser.last_used_at).toLocaleDateString(
                                    'en-US',
                                    {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    }
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="px-6 py-12 text-center">
                      <p className="text-sm text-gray-500">
                        No users have used this device yet
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Raw Data Tab */}
              {activeTab === 'raw-data' && (
                <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          Raw Device Data
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                          Showing{' '}
                          {totalRows > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0}{' '}
                          - {Math.min(currentPage * rowsPerPage, totalRows)} of{' '}
                          {totalRows} records
                        </p>
                      </div>
                    </div>
                  </div>

                  {rawDataRows.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-highlight-green">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                Timestamp
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                pH (mV)
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                pH Value
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                Temp (Raw)
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                Temp (°C)
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                Battery (mV)
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {rawDataRows.map((row, idx) => (
                              <tr key={idx}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {new Date(row.recorded_at).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {row.ph_voltage_mv}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {row.calibrated_ph.toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {row.temperature_raw}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {row.calibrated_temp.toFixed(1)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {row.battery_voltage_mv}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  0x
                                  {row.status_bits?.toString(16).padStart(2, '0') ||
                                    '00'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination Controls */}
                      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                        <div className="flex-1 flex justify-between sm:hidden">
                          <button
                            onClick={() =>
                              setCurrentPage(Math.max(1, currentPage - 1))
                            }
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() =>
                              setCurrentPage(
                                Math.min(
                                  Math.ceil(totalRows / rowsPerPage),
                                  currentPage + 1
                                )
                              )
                            }
                            disabled={
                              currentPage >= Math.ceil(totalRows / rowsPerPage)
                            }
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm text-gray-700">
                              Page{' '}
                              <span className="font-medium">{currentPage}</span> of{' '}
                              <span className="font-medium">
                                {Math.ceil(totalRows / rowsPerPage)}
                              </span>
                            </p>
                          </div>
                          <div>
                            <nav
                              className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                              aria-label="Pagination"
                            >
                              <button
                                onClick={() =>
                                  setCurrentPage(Math.max(1, currentPage - 1))
                                }
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <span className="sr-only">Previous</span>
                                <svg
                                  className="h-5 w-5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>

                              {/* Page numbers */}
                              {Array.from(
                                {
                                  length: Math.min(
                                    5,
                                    Math.ceil(totalRows / rowsPerPage)
                                  )
                                },
                                (_, i) => {
                                  const totalPages = Math.ceil(
                                    totalRows / rowsPerPage
                                  )
                                  let pageNum

                                  if (totalPages <= 5) {
                                    pageNum = i + 1
                                  } else if (currentPage <= 3) {
                                    pageNum = i + 1
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i
                                  } else {
                                    pageNum = currentPage - 2 + i
                                  }

                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => setCurrentPage(pageNum)}
                                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                        currentPage === pageNum
                                          ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                      }`}
                                    >
                                      {pageNum}
                                    </button>
                                  )
                                }
                              )}

                              <button
                                onClick={() =>
                                  setCurrentPage(
                                    Math.min(
                                      Math.ceil(totalRows / rowsPerPage),
                                      currentPage + 1
                                    )
                                  )
                                }
                                disabled={
                                  currentPage >= Math.ceil(totalRows / rowsPerPage)
                                }
                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <span className="sr-only">Next</span>
                                <svg
                                  className="h-5 w-5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>
                            </nav>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="px-6 py-12 text-center">
                      <p className="text-sm text-gray-500">No raw data available</p>
                    </div>
                  )}
                </div>
              )}

              {/* Tests Tab */}
              {activeTab === 'tests' && (
                <DeviceTestsTab tests={deviceTests} loading={testsLoading} />
              )}
            </div>
          )}
        </div>
      </main>

      {/* Toast Notifications */}
      <Toast
        message={errorMessage}
        type="error"
        onClose={() => setErrorMessage(null)}
      />
    </div>
  )
}
